import { useState, useEffect } from 'react';

import { Formik, Form, Field as FormikField } from 'formik';
import * as Yup from 'yup';
import { TextField } from 'formik-mui';
import FormHelperText from '@mui/material/FormHelperText';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { Button, Dropdown } from '../shared';

import { contactUsDomain } from '../../domain';

const leadGenInitialValues = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    roomType: '',
    furnishing: '',
    sizing: '',
};

const leadGenValidationSchema = Yup.object({
    firstName: Yup.string('').required('Firstname is required'),
    lastName: Yup.string('').required('Lastname is required'),
    email: Yup.string('').email('Enter a valid email').required('Email is required'),
    phoneNumber: Yup.string('')
        .matches(/^[0-9,+]*$/, 'Phone number is invalid')
        .min(9, 'Phone number is too short (minimum 9)')
        .max(15, 'Phone number is too long (maximum 15)')
        .required('Phone number is required'),
    roomType: Yup.string('').required('Room type category is required'),
    furnishing: Yup.string('').required('Please select a furnishing option'),
    sizing: Yup.string('').required('Please select a size option'),
});

const LeadGenForm = (props) => {

    const { campaignName, facebookEventOptions, setRelativeHeight, campaignData } = props;    

    const [contactUsSubmitError, setContactUsSubmitError] = useState(null);
    const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
    const [selectedRoomTypeCategory, setSelectedRoomTypeCategory] = useState(null);
    const [selectedFurnishing, setSelectedFurnishing] = useState(null);
    const [selectedSize, setSelectedSize] = useState(null);
    const [roomTypeCategories, setRoomTypeCategories] = useState(null);
    const [furnishingOptions, setFurnishingOptions] = useState(null);
    const [sizingOptions, setSizingOptions] = useState(null);
    const [sizingOptionsWithPrices, setSizingOptionsWithPrices] = useState(null);
    const [estimatedPrice, setEstimatedPrice] = useState(null);    
    
    const isProd = process.env.NODE_ENV === 'production';

    useEffect(() => {
        buildRoomTypeCategories();
    }, []);

    useEffect(() => {
        if (selectedRoomTypeCategory) {
            buildFurnitureOptionsForSelectedRoomType();
        }
    }, [selectedRoomTypeCategory]);

    useEffect(() => {
        if (selectedFurnishing) {
            sizeOptionsForSelectedFurnishedOptions();
        }
    }, [selectedFurnishing]);

    useEffect(() => {}, [selectedSize, setSelectedFurnishing]);

    const buildRoomTypeCategories = () => {
        const categories = [];
        _.forEach(_.get(campaignData, 'categories'), (category) => {
            const obj = {
                label: _.get(category, 'name'),
                value: _.get(category, 'name'),
                id: `${_.get(category, 'name')}-select`,
            };
            categories.push(obj);
        });
        setRoomTypeCategories(categories);
    };

    const buildFurnitureOptionsForSelectedRoomType = () => {
        const furnishingOptionsArr = [];
        const roomTypeOptions = _.find(_.get(campaignData, 'categories'), {
            name: selectedRoomTypeCategory,
        });

        if (roomTypeOptions) {
            _.forEach(roomTypeOptions, (val, key) => {
                if (key !== 'name' && val) {
                    const obj = {
                        label: _.upperFirst(key),
                        value: key,
                        id: `${key}-select`,
                    };
                    furnishingOptionsArr.push(obj);
                }
            });

            setFurnishingOptions(furnishingOptionsArr);
        }
    };

    const sizeOptionsForSelectedFurnishedOptions = () => {
        const sizingOptions = [];
        const roomTypeOptions = _.find(_.get(campaignData, 'categories'), {
            name: selectedRoomTypeCategory,
        });

        const options = roomTypeOptions[selectedFurnishing.toString()];
        setSizingOptionsWithPrices(options);

        if (options) {
            _.forEach(options, (val, key) => {
                if (key !== 'name' && val) {
                    const obj = {
                        label: _.upperFirst(key),
                        value: key,
                        id: `${key}-select`,
                    };
                    sizingOptions.push(obj);
                }
            });

            setSizingOptions(sizingOptions);
        }
    };

    const onRoomTypeCategoryChanged = (roomTypeCategory, event, formikOnChange) => {
        setSelectedRoomTypeCategory(roomTypeCategory);
        if (roomTypeCategory && setRelativeHeight) {
            setRelativeHeight(700);
        }
        setSelectedSize(null);
        setSelectedFurnishing(null);
        formikOnChange(event);
    };

    const onFurnishingChanged = (furnishing, event, formikOnChange) => {
        if (furnishing && setRelativeHeight) {
            setRelativeHeight(750);
        }
        setSelectedFurnishing(furnishing);
        setSelectedSize(null);
        formikOnChange(event);
    };

    const onSizingChanged = (sizing, event, formikOnChange) => {
        setSelectedSize(sizing);
        if (sizing && setRelativeHeight) {
            setRelativeHeight(800);
        }
        setEstimatedPrice(sizingOptionsWithPrices[sizing.toString()]);

        formikOnChange(event);
    };

    const onSubmit = async ({
        firstName,
        lastName,
        email,
        phoneNumber,
        roomType,
        furnishing,
        sizing,
    }) => {

        try {

            await contactUsDomain.submitLeadFromMarketingCampaign(
                firstName,
                lastName,
                phoneNumber,
                email,
                campaignName,
                roomType,
                furnishing,
                estimatedPrice,
                sizing,
            );

            // eslint-disable-next-line global-require
            const ReactPixel = require('react-facebook-pixel');
            if (isProd) {
                ReactPixel.default.track('Lead', facebookEventOptions);
            }

            setContactUsSubmitError(null);
            setSuccessfullySubmitted(true);
        } catch (error) {
            setContactUsSubmitError(error.message);
            setSuccessfullySubmitted(false);
        }
    };

    const ThankYouMessage = () => {

        if (!successfullySubmitted) {
            return null;
        }

        return (

            <div className='lead-gen-form-card-container'>

                <div className='get-in-touch-form-thank-you-container get-in-touch-form-thank-you-height-80'>

                    <h3>Thanks for Getting in Touch!</h3>
                    <p>Someone from HOMii will be in touch ASAP</p>

                </div>

            </div>

        );
    };

    const renderDesktopForm = () => {

        if (successfullySubmitted) {
            return null;
        }

        return (

            <div className='lead-gen-form-container'>

                <div>

                    <Formik
                        initialValues={leadGenInitialValues}
                        validationSchema={leadGenValidationSchema}
                        onSubmit={onSubmit}
                        validateOnBlur
                        validateOnChange={false}
                    >
                        {({
                            submitForm,
                            isSubmitting,
                            touched,
                            errors,
                            handleChange,
                            handleBlur,
                        }) => (

                            <Form>

                                <FormikField className='lead-gen-form-field'
                                    component={TextField}
                                    required
                                    name="firstName"
                                    id="firstName"
                                    type="text"
                                    label="Name"
                                    variant="outlined"
                                    color="secondary"
                                    helperText={touched.firstName ? errors.firstName : ''}
                                    error={touched.firstName && !!errors.firstName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                />

                                <FormikField className='lead-gen-form-field'
                                    component={TextField}
                                    required
                                    name="lastName"
                                    id="lastName"
                                    type="text"
                                    label="Surname"
                                    variant="outlined"
                                    color="secondary"
                                    helperText={touched.lastName ? errors.lastName : ''}
                                    error={touched.lastName && !!errors.lastName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                />

                                <FormikField className='lead-gen-form-field'
                                    component={TextField}
                                    required
                                    name="phoneNumber"
                                    id="phoneNumber"
                                    type="tel"
                                    label="Contact Number"
                                    variant="outlined"
                                    color="secondary"
                                    helperText={touched.phoneNumber ? errors.phoneNumber : ''}
                                    error={touched.phoneNumber && !!errors.phoneNumber}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                />

                                <FormikField className='lead-gen-form-field'
                                    component={TextField}
                                    required
                                    name="email"
                                    id="email"
                                    type="email"
                                    label="Email Address"
                                    variant="outlined"
                                    color="secondary"
                                    helperText={touched.email ? errors.email : ''}
                                    error={touched.email && !!errors.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                />

                                <div className='lead-gen-form-dropdown-container'>
                                    <Dropdown
                                        placeholder="Room Type Category *"
                                        selectId="roomtype_category_dropdown"
                                        options={roomTypeCategories}
                                        onChange={(_roomTypeCategory, event) => {
                                            onRoomTypeCategoryChanged(
                                                _roomTypeCategory,
                                                event,
                                                handleChange,
                                            );
                                        }}
                                        name="roomType"
                                        defaultValue={selectedRoomTypeCategory}
                                        helperText={touched.roomType ? errors.roomType : ''}
                                        error={touched.roomType && !!errors.roomType}
                                        handleBlur={handleBlur}
                                    />
                                </div>

                                {selectedRoomTypeCategory && (
                                    <div className='lead-gen-form-dropdown-container'>
                                        <Dropdown
                                            placeholder="Furniture Preference *"
                                            selectId="furniture_dropdown"
                                            options={furnishingOptions}
                                            onChange={(_furnishing, event) => {
                                                onFurnishingChanged(
                                                    _furnishing,
                                                    event,
                                                    handleChange,
                                                );
                                            }}
                                            name="furnishing"
                                            defaultValue={selectedFurnishing}
                                            helperText={touched.furnishing ? errors.furnishing : ''}
                                            error={touched.furnishing && !!errors.furnishing}
                                            handleBlur={handleBlur}
                                        />
                                    </div>
                                )}

                                {selectedFurnishing && (
                                    <div className='lead-gen-form-dropdown-container'>
                                        <Dropdown
                                            placeholder="Size Preference *"
                                            selectId="size_dropdown"
                                            options={sizingOptions}
                                            onChange={(_sizing, event) => {
                                                onSizingChanged(_sizing, event, handleChange);
                                            }}
                                            name="sizing"
                                            defaultValue={selectedSize}
                                            helperText={touched.sizing ? errors.sizing : ''}
                                            error={touched.sizing && !!errors.sizing}
                                            handleBlur={handleBlur}
                                        />
                                    </div>
                                )}

                                {selectedSize && estimatedPrice && (
                                    <div className='lead-gen-form-price-container'>
                                        <h6>Starting from </h6>
                                        <h5>R{estimatedPrice}</h5>
                                    </div>
                                )}
                                
                                <FormHelperText error>{contactUsSubmitError}</FormHelperText>

                                <div className='get-in-touch-form-button-container'>

                                    <Button                                                                             
                                        variant="contained"
                                        color="primary"
                                        isLoading={isSubmitting}
                                        onClick={submitForm}
                                        id="lead-form-submit"
                                    >
                                        Submit
                                    </Button>

                                </div>

                            </Form>
                        )}

                    </Formik>

                </div>

            </div>
        );
    };

    return (

        <div>

            {!successfullySubmitted && (

                <div className='lead-gen-form-card-container'>

                    <div className='get-in-touch-form-header'>

                        <h3>Get in Touch</h3>

                        <p className='lead-gen-form-paragraph'>

                            Contact us to start your best HOMii life.

                        </p>

                    </div>

                    {renderDesktopForm()}

                </div>
            )}

            <ThankYouMessage />

        </div>
    );
};

export default LeadGenForm;

LeadGenForm.propTypes = {
    campaignName: PropTypes.string,
    formRoomTypeCategoriesData: PropTypes.array,
    formFurnishingOptionsData: PropTypes.array,
    facebookEventOptions: PropTypes.object,
    isAbsolute: PropTypes.bool,
    campaignData: PropTypes.object,

    setRelativeHeight: PropTypes.func,
};

